/* Utilities for iframe <-> parent messaging used by HC SDK and HC Viewer */
export type ZoomAction = "in" | "out";
export type DrawModeAction =
    | "shaded"
    | "wireframe"
    | "hidden-line"
    | "shaded-wire"
    | "xray"
    | "ghosting";

export interface ZoomPayload {
    action: ZoomAction;
    percent: number;
}

export interface DrawModePayload {
    mode: DrawModeAction;
}

export interface ExplodePayload {
    magnitude: number;
}

export type MarkupAction =
    | "line"
    | "arrow"
    | "circle"
    | "ellipse"
    | "rectangle"
    | "polygon"
    | "polyline"
    | "textbox"
    | "note"
    | "callout"
    | "cloud"
    | "freehand";

export interface MarkupActionPayload {
    action: MarkupAction;
}

export interface MarkupRequestPayload {
    requestId: string;
}

export interface MarkupOperationResultPayload {
    requestId: string;
    success: boolean;
    timestamp: number;
    error?: string;
}

export interface MarkupListItem {
    id: string;
    viewId: string;
    viewName?: string;
    title: string;
    type: string;
    shapeName?: string;
    createdDate?: string;
    modifiedDate?: string;
    createdBy?: string;
    lastModifiedBy?: string;
}

export interface MarkupListPayload {
    requestId: string;
    markups: MarkupListItem[];
    timestamp: number;
}

export interface NodeSelectPayload {
    nodeId: string;
    timestamp: number;
}

export interface NodeSelectionChangePayload {
    nodeIds: string[];
    timestamp: number;
}

export type ViewerLoadingPhase = "loading" | "ready" | "error";

export interface ViewerLoadingChangePayload {
    loading: boolean;
    phase: ViewerLoadingPhase;
    viewId?: string;
    modelFileId?: string;
    timestamp?: number;
}

export interface ViewerReadyPayload {
    ready: true;
    viewId?: string;
    modelFileId?: string;
    timestamp: number;
}

export type ToolbarMessageFormat = "3d" | "pdf";
export type ToolbarUseTarget = "all" | "left" | "center" | "right";

export interface ToolbarConfigPayload {
    format: ToolbarMessageFormat;
    mode: "set" | "clear";
    operators?: string[];
}

export interface ToolbarUsePayload {
    target?: ToolbarUseTarget;
}

export interface PanelOpenPayload {
    panel:
        | "clipping-commands"
        | "setting"
        | "statesObjects"
        | "linkedObjects"
        | "model-tree"
        | "sheets"
        | "object-properties";
    format?: ToolbarMessageFormat;
}

export interface PanelClosePayload {
    panel:
        | "clipping-commands"
        | "setting"
        | "statesObjects"
        | "linkedObjects"
        | "model-tree"
        | "sheets"
        | "object-properties";
    format?: ToolbarMessageFormat;
}

export type CuttingPlaneAction =
    | "close"
    | "multi"
    | "toggle-section"
    | "toggle-plane"
    | "plane-x"
    | "plane-y"
    | "plane-z"
    | "plane-box"
    | "rotate-box"
    | "reverse-plane-x"
    | "reverse-plane-y"
    | "reverse-plane-z";

export interface CuttingPlaneActionPayload {
    action: CuttingPlaneAction;
}

export interface TreeSelectNodePayload {
    nodeId?: string;
    nodeIds?: string[];
}

export interface TreeSetNodeVisibilityPayload {
    nodeIds: string[];
    visible: boolean;
}

export interface SheetsGetListPayload {
    requestId: string;
}

export interface SheetsApplyPayload {
    sheetId: string | number;
}

export interface ObjectPropertiesGetListPayload {
    requestId: string;
    nodeIds: string[];
    expandToRealNodes?: boolean;
    includeSelectionMeta?: boolean;
}

export interface ObjectPropertiesListPayload {
    requestId: string;
    selectionKey: string;
    nodeIds: string[];
    persistentIds: string[];
    properties: Record<string, unknown>[];
    timestamp: number;
}

export interface LinkedObjectsGetListPayload {
    requestId: string;
}

export interface StatesObjectsGetListPayload {
    requestId: string;
}

export interface TreeGetNodeIdsPayload {
    requestId: string;
    onlyRealNodes?: boolean;
}

export interface TreeGetNodesPayload {
    requestId: string;
    onlyRealNodes?: boolean;
}

export interface CameraGetZoomPayload {
    requestId: string;
}

export interface PdfModeEventPayload {
    mode: "plan" | "document";
    timestamp: number;
}

export interface PdfToolbarActionEventPayload {
    timestamp: number;
}

export interface PdfCurrentPagePayload {
    pageIndex: number;
    pageNumber: number;
    timestamp: number;
}

export type LanguageCode = "en" | "vn";

export interface LanguageChangePayload {
    language: LanguageCode;
    timestamp?: number;
}

export type IframeMessage = {
    source: "HC_VIEWER" | string;
    type: string;
    payload?: any;
};

function getReferrerOrigin(): string | null {
    try {
        return document.referrer ? new URL(document.referrer).origin : null;
    } catch (e) {
        return null;
    }
}

export function getTargetOrigin(): string {
    const ref = getReferrerOrigin();
    return ref || "*";
}

export function postMessageToParent(
    message: IframeMessage,
    targetOrigin?: string
): void {
    try {
        const origin = targetOrigin || getTargetOrigin();
        // best-effort postMessage to parent
        window.parent.postMessage(message, origin);
    } catch (e) {
        // swallow errors in production usage
    }
}

export function postMessageToIframe(
    iframe: HTMLIFrameElement | Window,
    message: IframeMessage,
    targetOrigin?: string
): void {
    try {
        const win: Window | null =
            iframe instanceof HTMLIFrameElement
                ? iframe.contentWindow
                : (iframe as Window);
        if (!win) return;
        const origin = targetOrigin || getTargetOrigin() || "*";
        win.postMessage(message, origin);
    } catch (e) {
        // swallow
    }
}

export function makeZoomMessage(
    action: ZoomAction,
    percent: number
): IframeMessage {
    return {
        source: "HC_SDK",
        type: "viewer-zoom",
        payload: { action, percent },
    };
}

export function makeNodeSelectMessage(nodeId: string): IframeMessage {
    return {
        source: "HC_VIEWER",
        type: "viewer-node-select",
        payload: { nodeId, timestamp: Date.now() },
    };
}

export function makeNodeSelectionChangeMessage(
    nodeIds: string[]
): IframeMessage {
    return {
        source: "HC_VIEWER",
        type: "viewer-node-selection-change",
        payload: { nodeIds, timestamp: Date.now() },
    };
}

export function makeViewerLoadingChangeMessage(
    payload: ViewerLoadingChangePayload
): IframeMessage {
    return {
        source: "HC_VIEWER",
        type: "viewer-loading-change",
        payload: { ...payload, timestamp: payload.timestamp ?? Date.now() },
    };
}

export function makeViewerReadyMessage(
    payload: Omit<ViewerReadyPayload, "ready" | "timestamp"> & {
        timestamp?: number;
    } = {}
): IframeMessage {
    return {
        source: "HC_VIEWER",
        type: "viewer-ready",
        payload: {
            ...payload,
            ready: true,
            timestamp: payload.timestamp ?? Date.now(),
        },
    };
}

export function makeHomeMessage(): IframeMessage {
    return {
        source: "HC_SDK",
        type: "viewer-home",
        payload: {},
    };
}

export function makeHomeClickedMessage(): IframeMessage {
    return {
        source: "HC_VIEWER",
        type: "viewer-home-click",
        payload: { timestamp: Date.now() },
    };
}

export function makePanToggleMessage(enabled: boolean): IframeMessage {
    return {
        source: "HC_SDK",
        type: "viewer-pan-toggle",
        payload: { enabled },
    };
}

export function makePanChangeMessage(enabled: boolean): IframeMessage {
    return {
        source: "HC_VIEWER",
        type: "viewer-pan-change",
        payload: { enabled, timestamp: Date.now() },
    };
}

export function makePdfModeEventMessage(
    mode: "plan" | "document"
): IframeMessage {
    return {
        source: "HC_VIEWER",
        type:
            mode === "plan"
                ? "viewer-pdf-plan-mode"
                : "viewer-pdf-document-mode",
        payload: { mode, timestamp: Date.now() },
    };
}

export function makePdfPageActionMessage(
    action: "first-page" | "previous-page" | "next-page" | "last-page"
): IframeMessage {
    const typeMap: Record<typeof action, string> = {
        "first-page": "viewer-pdf-first-page",
        "previous-page": "viewer-pdf-previous-page",
        "next-page": "viewer-pdf-next-page",
        "last-page": "viewer-pdf-last-page",
    };
    return {
        source: "HC_VIEWER",
        type: typeMap[action],
        payload: { timestamp: Date.now() },
    };
}

export function makePdfCurrentPageMessage(
    pageIndex: number,
    pageNumber: number
): IframeMessage {
    return {
        source: "HC_VIEWER",
        type: "viewer-pdf-current-page",
        payload: { pageIndex, pageNumber, timestamp: Date.now() },
    };
}

export function isLanguageChangeMessage(data: any): data is {
    source: string;
    type: "viewer-language-change";
    payload: LanguageChangePayload;
} {
    if (
        !data ||
        data.source !== "HC_SDK" ||
        data.type !== "viewer-language-change"
    )
        return false;
    const language = data.payload?.language;
    return language === "en" || language === "vn";
}

export function isHomeMessage(
    data: any
): data is { source: string; type: "viewer-home" } {
    return !!data && data.source === "HC_SDK" && data.type === "viewer-home";
}

export function isPanToggleMessage(data: any): data is {
    source: string;
    type: "viewer-pan-toggle";
    payload?: { enabled?: boolean };
} {
    if (!data || data.source !== "HC_SDK" || data.type !== "viewer-pan-toggle")
        return false;
    if (typeof data.payload === "undefined") return true;
    if (data.payload === null || typeof data.payload !== "object") return false;
    if (typeof data.payload.enabled === "undefined") return true;
    return typeof data.payload.enabled === "boolean";
}

export function isSelectMessage(
    data: any
): data is { source: string; type: "viewer-select" } {
    return !!data && data.source === "HC_SDK" && data.type === "viewer-select";
}

export function isAreaSelectMessage(
    data: any
): data is { source: string; type: "viewer-area-select" } {
    return (
        !!data && data.source === "HC_SDK" && data.type === "viewer-area-select"
    );
}

export function isOrbitMessage(
    data: any
): data is { source: string; type: "viewer-orbit" } {
    return !!data && data.source === "HC_SDK" && data.type === "viewer-orbit";
}

export function isRotateZMessage(
    data: any
): data is { source: string; type: "viewer-rotate-z" } {
    return (
        !!data && data.source === "HC_SDK" && data.type === "viewer-rotate-z"
    );
}

export function isWalkThroughMessage(
    data: any
): data is { source: string; type: "viewer-walk-through" } {
    return (
        !!data &&
        data.source === "HC_SDK" &&
        data.type === "viewer-walk-through"
    );
}

export function isZoomWindowMessage(
    data: any
): data is { source: string; type: "viewer-zoom-window" } {
    return (
        !!data && data.source === "HC_SDK" && data.type === "viewer-zoom-window"
    );
}

export function isZoomFitMessage(
    data: any
): data is { source: string; type: "viewer-zoom-fit" } {
    return (
        !!data && data.source === "HC_SDK" && data.type === "viewer-zoom-fit"
    );
}

export function isToolbarConfigMessage(data: any): data is {
    source: string;
    type: "viewer-toolbar-config";
    payload: ToolbarConfigPayload;
} {
    if (
        !data ||
        data.source !== "HC_SDK" ||
        data.type !== "viewer-toolbar-config"
    )
        return false;
    const payload = data.payload;
    if (!payload || typeof payload !== "object") return false;
    if (payload.format !== "3d" && payload.format !== "pdf") return false;
    if (payload.mode !== "set" && payload.mode !== "clear") return false;
    if (payload.operators !== undefined && !Array.isArray(payload.operators))
        return false;
    return true;
}

export function isToolbarUseMessage(data: any): data is {
    source: string;
    type: "viewer-toolbar-use";
    payload: ToolbarUsePayload;
} {
    if (!data || data.source !== "HC_SDK" || data.type !== "viewer-toolbar-use")
        return false;
    const payload = data.payload;
    if (!payload || typeof payload !== "object") return false;
    if (
        payload.target !== undefined &&
        !["all", "left", "center", "right"].includes(payload.target)
    )
        return false;
    return true;
}

export function isPanelOpenMessage(data: any): data is {
    source: string;
    type: "viewer-panel-open";
    payload: PanelOpenPayload;
} {
    if (!data || data.source !== "HC_SDK" || data.type !== "viewer-panel-open")
        return false;
    const payload = data.payload;
    if (!payload || typeof payload !== "object") return false;
    if (
        ![
            "clipping-commands",
            "setting",
            "statesObjects",
            "linkedObjects",
            "model-tree",
            "sheets",
            "object-properties",
        ].includes(payload.panel)
    )
        return false;
    if (
        payload.format !== undefined &&
        payload.format !== "3d" &&
        payload.format !== "pdf"
    )
        return false;
    return true;
}

export function isPanelCloseMessage(data: any): data is {
    source: string;
    type: "viewer-panel-close";
    payload: PanelClosePayload;
} {
    if (!data || data.source !== "HC_SDK" || data.type !== "viewer-panel-close")
        return false;
    const payload = data.payload;
    if (!payload || typeof payload !== "object") return false;
    if (
        ![
            "clipping-commands",
            "setting",
            "statesObjects",
            "linkedObjects",
            "model-tree",
            "sheets",
            "object-properties",
        ].includes(payload.panel)
    )
        return false;
    if (
        payload.format !== undefined &&
        payload.format !== "3d" &&
        payload.format !== "pdf"
    )
        return false;
    return true;
}

export function isCuttingPlaneActionMessage(data: any): data is {
    source: string;
    type: "viewer-cutting-plane-action";
    payload: CuttingPlaneActionPayload;
} {
    if (
        !data ||
        data.source !== "HC_SDK" ||
        data.type !== "viewer-cutting-plane-action"
    )
        return false;
    const payload = data.payload;
    if (!payload || typeof payload !== "object") return false;
    return [
        "close",
        "multi",
        "toggle-section",
        "toggle-plane",
        "plane-x",
        "plane-y",
        "plane-z",
        "plane-box",
        "rotate-box",
        "reverse-plane-x",
        "reverse-plane-y",
        "reverse-plane-z",
    ].includes(payload.action);
}

export function isTreeSelectNodeMessage(data: any): data is {
    source: string;
    type: "viewer-tree-select-node";
    payload: TreeSelectNodePayload;
} {
    if (
        !data ||
        data.source !== "HC_SDK" ||
        data.type !== "viewer-tree-select-node"
    )
        return false;
    const payload = data.payload;
    if (!payload || typeof payload !== "object") return false;
    const hasNodeId =
        typeof payload.nodeId === "string" && payload.nodeId.trim() !== "";
    const hasNodeIds =
        Array.isArray(payload.nodeIds) &&
        (payload.nodeIds.length === 0 ||
            payload.nodeIds.every(
                (nodeId: unknown) =>
                    typeof nodeId === "string" && nodeId.trim() !== ""
            ));
    return hasNodeId || hasNodeIds;
}

export function isTreeSetNodeVisibilityMessage(data: any): data is {
    source: string;
    type: "viewer-tree-set-node-visibility";
    payload: TreeSetNodeVisibilityPayload;
} {
    if (
        !data ||
        data.source !== "HC_SDK" ||
        data.type !== "viewer-tree-set-node-visibility"
    )
        return false;
    const payload = data.payload;
    if (!payload || typeof payload !== "object") return false;
    if (!Array.isArray(payload.nodeIds) || payload.nodeIds.length === 0)
        return false;
    if (
        !payload.nodeIds.every(
            (nodeId: unknown) =>
                typeof nodeId === "string" && nodeId.trim() !== ""
        )
    )
        return false;
    if (typeof payload.visible !== "boolean") return false;
    return true;
}

export function isSheetsGetListMessage(data: any): data is {
    source: string;
    type: "viewer-sheets-get-list";
    payload: SheetsGetListPayload;
} {
    if (
        !data ||
        data.source !== "HC_SDK" ||
        data.type !== "viewer-sheets-get-list"
    )
        return false;
    const payload = data.payload;
    if (!payload || typeof payload !== "object") return false;
    if (
        typeof payload.requestId !== "string" ||
        payload.requestId.trim() === ""
    )
        return false;
    return true;
}

export function isObjectPropertiesGetListMessage(data: any): data is {
    source: string;
    type: "viewer-object-properties-get-list";
    payload: ObjectPropertiesGetListPayload;
} {
    if (
        !data ||
        data.source !== "HC_SDK" ||
        data.type !== "viewer-object-properties-get-list"
    )
        return false;
    const payload = data.payload;
    if (!payload || typeof payload !== "object") return false;
    if (
        typeof payload.requestId !== "string" ||
        payload.requestId.trim() === ""
    )
        return false;
    if (!Array.isArray(payload.nodeIds)) return false;
    if (
        !payload.nodeIds.every(
            (nodeId: unknown) =>
                typeof nodeId === "string" && nodeId.trim() !== ""
        )
    )
        return false;
    if (
        payload.expandToRealNodes !== undefined &&
        typeof payload.expandToRealNodes !== "boolean"
    )
        return false;
    if (
        payload.includeSelectionMeta !== undefined &&
        typeof payload.includeSelectionMeta !== "boolean"
    )
        return false;
    return true;
}

export function isLinkedObjectsGetListMessage(data: any): data is {
    source: string;
    type: "viewer-linked-objects-get-list";
    payload: LinkedObjectsGetListPayload;
} {
    if (
        !data ||
        data.source !== "HC_SDK" ||
        data.type !== "viewer-linked-objects-get-list"
    )
        return false;
    const payload = data.payload;
    if (!payload || typeof payload !== "object") return false;
    if (
        typeof payload.requestId !== "string" ||
        payload.requestId.trim() === ""
    )
        return false;
    return true;
}

export function isStatesObjectsGetListMessage(data: any): data is {
    source: string;
    type: "viewer-states-objects-get-list";
    payload: StatesObjectsGetListPayload;
} {
    if (
        !data ||
        data.source !== "HC_SDK" ||
        data.type !== "viewer-states-objects-get-list"
    )
        return false;
    const payload = data.payload;
    if (!payload || typeof payload !== "object") return false;
    if (
        typeof payload.requestId !== "string" ||
        payload.requestId.trim() === ""
    )
        return false;
    return true;
}

export function isSheetsApplyMessage(data: any): data is {
    source: string;
    type: "viewer-sheets-apply";
    payload: SheetsApplyPayload;
} {
    if (
        !data ||
        data.source !== "HC_SDK" ||
        data.type !== "viewer-sheets-apply"
    )
        return false;
    const payload = data.payload;
    if (!payload || typeof payload !== "object") return false;
    const value = payload.sheetId;
    if (typeof value === "number" && Number.isFinite(value)) return true;
    if (typeof value === "string" && value.trim() !== "") return true;
    return false;
}

export function isTreeGetNodeIdsMessage(data: any): data is {
    source: string;
    type: "viewer-tree-get-node-ids";
    payload: TreeGetNodeIdsPayload;
} {
    if (
        !data ||
        data.source !== "HC_SDK" ||
        data.type !== "viewer-tree-get-node-ids"
    )
        return false;
    const payload = data.payload;
    if (!payload || typeof payload !== "object") return false;
    if (
        typeof payload.requestId !== "string" ||
        payload.requestId.trim() === ""
    )
        return false;
    if (
        payload.onlyRealNodes !== undefined &&
        typeof payload.onlyRealNodes !== "boolean"
    )
        return false;
    return true;
}

export function isTreeGetNodesMessage(data: any): data is {
    source: string;
    type: "viewer-tree-get-nodes";
    payload: TreeGetNodesPayload;
} {
    if (
        !data ||
        data.source !== "HC_SDK" ||
        data.type !== "viewer-tree-get-nodes"
    )
        return false;
    const payload = data.payload;
    if (!payload || typeof payload !== "object") return false;
    if (
        typeof payload.requestId !== "string" ||
        payload.requestId.trim() === ""
    )
        return false;
    if (
        payload.onlyRealNodes !== undefined &&
        typeof payload.onlyRealNodes !== "boolean"
    )
        return false;
    return true;
}

export function isCameraGetZoomMessage(data: any): data is {
    source: string;
    type: "viewer-camera-get-zoom";
    payload: CameraGetZoomPayload;
} {
    if (
        !data ||
        data.source !== "HC_SDK" ||
        data.type !== "viewer-camera-get-zoom"
    )
        return false;
    const payload = data.payload;
    if (!payload || typeof payload !== "object") return false;
    return (
        typeof payload.requestId === "string" && payload.requestId.trim() !== ""
    );
}

export function isMarkupActionMessage(data: any): data is {
    source: string;
    type: "viewer-markup-action";
    payload: MarkupActionPayload;
} {
    if (
        !data ||
        data.source !== "HC_SDK" ||
        data.type !== "viewer-markup-action"
    )
        return false;
    const action = data.payload?.action;
    return [
        "line",
        "arrow",
        "circle",
        "ellipse",
        "rectangle",
        "polygon",
        "polyline",
        "textbox",
        "note",
        "callout",
        "cloud",
        "freehand",
    ].includes(action);
}

export function isMarkupSaveMessage(data: any): data is {
    source: string;
    type: "viewer-markup-save";
    payload: MarkupRequestPayload;
} {
    if (!data || data.source !== "HC_SDK" || data.type !== "viewer-markup-save")
        return false;
    const requestId = data.payload?.requestId;
    return typeof requestId === "string" && requestId.trim() !== "";
}

export function isMarkupCancelMessage(data: any): data is {
    source: string;
    type: "viewer-markup-cancel";
    payload: MarkupRequestPayload;
} {
    if (
        !data ||
        data.source !== "HC_SDK" ||
        data.type !== "viewer-markup-cancel"
    )
        return false;
    const requestId = data.payload?.requestId;
    return typeof requestId === "string" && requestId.trim() !== "";
}

export function isMarkupGetListMessage(data: any): data is {
    source: string;
    type: "viewer-markup-get-list";
    payload: MarkupRequestPayload;
} {
    if (
        !data ||
        data.source !== "HC_SDK" ||
        data.type !== "viewer-markup-get-list"
    )
        return false;
    const requestId = data.payload?.requestId;
    return typeof requestId === "string" && requestId.trim() !== "";
}

export function isZoomMessage(
    data: any
): data is { source: string; type: "viewer-zoom"; payload: ZoomPayload } {
    return (
        !!data &&
        data.source === "HC_SDK" &&
        data.type === "viewer-zoom" &&
        data.payload &&
        typeof data.payload.percent === "number"
    );
}

export function isDrawModeMessage(data: any): data is {
    source: string;
    type: "viewer-draw-mode";
    payload: DrawModePayload;
} {
    if (!data || data.source !== "HC_SDK" || data.type !== "viewer-draw-mode")
        return false;
    const mode = data.payload?.mode;
    return (
        mode === "shaded" ||
        mode === "wireframe" ||
        mode === "hidden-line" ||
        mode === "shaded-wire" ||
        mode === "xray" ||
        mode === "ghosting"
    );
}

export function isExplodeMessage(
    data: any
): data is { source: string; type: "viewer-explode"; payload: ExplodePayload } {
    if (!data || data.source !== "HC_SDK" || data.type !== "viewer-explode")
        return false;
    const magnitude = data.payload?.magnitude;
    return (
        typeof magnitude === "number" &&
        Number.isFinite(magnitude) &&
        magnitude >= 0
    );
}

export function isNodeSelectMessage(data: any): data is {
    source: string;
    type: "viewer-node-select";
    payload: NodeSelectPayload;
} {
    return (
        !!data &&
        data.source === "HC_VIEWER" &&
        data.type === "viewer-node-select" &&
        data.payload &&
        typeof data.payload.nodeId === "string"
    );
}

export function isNodeSelectionChangeMessage(data: any): data is {
    source: string;
    type: "viewer-node-selection-change";
    payload: NodeSelectionChangePayload;
} {
    return (
        !!data &&
        data.source === "HC_VIEWER" &&
        data.type === "viewer-node-selection-change" &&
        data.payload &&
        Array.isArray(data.payload.nodeIds) &&
        data.payload.nodeIds.every(
            (nodeId: unknown) => typeof nodeId === "string"
        )
    );
}

export function isValidOrigin(
    event: MessageEvent,
    allowedOrigins?: string[]
): boolean {
    if (allowedOrigins && allowedOrigins.length)
        return allowedOrigins.includes(event.origin);
    const ref = getReferrerOrigin();
    if (ref) return event.origin === ref;
    return true; // fallback permissive when no referrer known
}

/**
 * Add a message listener with origin/source basic validation.
 * Returns an unsubscribe function.
 */
export function addMessageListener(
    handler: (evt: MessageEvent) => void,
    opts?: { allowedOrigins?: string[] }
): () => void {
    const wrapper = (evt: MessageEvent) => {
        try {
            if (!evt.data || typeof evt.data !== "object") return;
            if (!isValidOrigin(evt, opts?.allowedOrigins)) return;
            handler(evt);
        } catch (e) {
            // ignore
        }
    };
    window.addEventListener("message", wrapper);
    return () => window.removeEventListener("message", wrapper);
}
